import {
  BLOCK_STATUSES,
  type BlockStatus,
  CAPTURE_SESSION_STATUSES,
  type CaptureSession,
  READINESS_STATES,
  type ReadinessSummary,
  type WorkflowType,
} from '@/shared/capture/capture-session-types';

export function createCaptureSession(input: {
  id: string;
  workflowType: WorkflowType;
  locationId?: number;
  sourceTime: string;
}): CaptureSession {
  return {
    id: input.id,
    workflowType: input.workflowType,
    locationId: input.locationId,
    status: CAPTURE_SESSION_STATUSES.draft,
    data: {},
    blockStatuses: {},
    validationErrors: [],
    photos: [],
    aiExtractions: [],
    createdAt: input.sourceTime,
    updatedAt: input.sourceTime,
  };
}

export function summarizeReadiness(blockStatuses: Record<string, BlockStatus>): ReadinessSummary {
  const statuses = Object.values(blockStatuses);
  const validBlocks = statuses.filter(status => status === BLOCK_STATUSES.valid).length;
  const warningBlocks = statuses.filter(status => status === BLOCK_STATUSES.warning).length;
  const blockedBlocks = statuses.filter(
    status => status === BLOCK_STATUSES.blocked || status === BLOCK_STATUSES.empty,
  ).length;
  const state =
    blockedBlocks > 0
      ? READINESS_STATES.blocked
      : warningBlocks > 0
        ? READINESS_STATES.warning
        : READINESS_STATES.ready;

  return {
    state,
    requiredBlocks: statuses.length,
    validBlocks,
    warningBlocks,
    blockedBlocks,
  };
}

/** Deleting it cannot lose data saved in ENMON: never submitted, or submitted but never linked to an ENMON meter. */
export function isDeletableLocalSession(session: Pick<CaptureSession, '_id' | 'meterId' | 'status'>) {
  if (session.status === CAPTURE_SESSION_STATUSES.draft || session.status === CAPTURE_SESSION_STATUSES.ready) {
    return true;
  }

  const isUnsavedSubmission =
    session.status === CAPTURE_SESSION_STATUSES.queued ||
    session.status === CAPTURE_SESSION_STATUSES.syncFailed ||
    session.status === CAPTURE_SESSION_STATUSES.needsReview;

  return isUnsavedSubmission && !(session._id || session.meterId);
}

export function isSubmittedSession(session: Pick<CaptureSession, 'status'>) {
  return session.status !== CAPTURE_SESSION_STATUSES.draft && session.status !== CAPTURE_SESSION_STATUSES.ready;
}
